import React, { useState, useEffect } from "react";
import {
  Box,
  Image,
  Text,
  Badge,
  Flex,
  Icon,
  Button,
  Collapse,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  VStack,
  Spinner,
  useColorModeValue,
  SlideFade,
	StepTitle
} from "@chakra-ui/react";
import { FaStar, FaMapMarkerAlt, FaChevronDown, FaChevronUp, FaPalette } from "react-icons/fa";
import styled from "styled-components";
import axios from "axios";
import PhotographerDetail from "./PhotographerDetail2"; // ✅ Import booking component
import { useCart } from '../context/CartContext';
import { useToast } from '@chakra-ui/react';
import {jwtDecode} from 'jwt-decode';

const PhotographerPreviewModal = ({ isOpen, onClose, creator }) => {
  const { isOpen: detailsOpen, onToggle } = useDisclosure();
  const [media, setMedia] = useState({ photos: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null); // ✅ Selected image for full view
  const [viewMode, setViewMode] = useState("preview"); // ✅ "preview" or "booking"
	const { cartItems, setCartItems } = useCart();
  const toast = useToast();


  const bgColor = useColorModeValue("white", "black");
  const textColor = useColorModeValue("black", "white");

  

  useEffect(() => {
    if (isOpen && creator) {
      fetchMediaFiles();
    }
  }, [isOpen, creator]);

  const fetchMediaFiles = async () => {
    setLoading(true);
    setError("");

    try {
      const backendUrl =
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_BACKEND_URL
          : "http://localhost:8080";

      const response = await axios.get(`${backendUrl}/media/list/${creator.id}`);
      const mediaFiles = response.data.mediaFiles || [];

      let photos = mediaFiles
        .filter((file) => file.contentType.startsWith("image/"))
        .map((file) => ({
          ...file,
          displayUrl: file.compressedUrl, // Use compressed version if available
        }));

      // Ensure we have at least 10 images (if not, duplicate)
      while (photos.length < 10 && photos.length > 0) {
        photos = [...photos, ...photos].slice(0, 10);
      }

      setMedia({ photos });
    } catch (err) {
      console.error("Error fetching media files:", err);
      setError("Failed to load photos.");
    } finally {
      setLoading(false);
    }
  };

  if (!creator) return null;

  const getCardStyle = (index) => {
    const totalCards = 10;
    const angle = (360 / totalCards) * index;
    const delay = -2 * index;
    return {
      transform: `translate(-50%, -50%) rotateY(${angle}deg) translateZ(150px)`,
      animationDelay: `${delay}s`,
    };
  };

  const handleAddToCart = () => {
    console.log("CREATOR",creator)
    const token = localStorage.getItem('token');
    if (!token) {
      toast({
        title: 'Not logged in',
        description: 'Please log in to add to cart.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
  
    const decoded = jwtDecode(token);
  
    if (decoded.user_id === creator.id) {
      toast({
        title: 'Invalid action',
        description: 'You cannot book yourself.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
  
    // Dynamic user type (photographer, model, studio)
    const creatorType = creator.user_type; // ex: 'photographer' | 'model' | 'studio'
  
    const newItem = {
      id: creator.id,
      [creatorType]: creator,
    };
  
    const existingIndex = cartItems.findIndex(item => item.id === newItem.id);
    const updatedCart = [...cartItems];
  
    if (existingIndex !== -1) {
      updatedCart[existingIndex] = newItem;
      toast({
        title: 'Already in cart',
        description: `${creator.first_name || creator.name} is already in your cart. Info updated.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } else {
      updatedCart.push(newItem);
      toast({
        title: 'Added to cart!',
        description: `${creator.first_name || creator.name} has been added.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  
    setCartItems(updatedCart);
  };
  
  
  

  return (
    <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent
        position="fixed"
        top="10%"
        transform="translate(-50%, 0)"
        width={{ base: "95%", md: "80%", lg: "60%" }}
        maxW="420px"
        maxH="80vh"
        borderRadius="xl"
        overflow="hidden"
        boxShadow="xl"
        p={0}
      >
        <ModalCloseButton onClick={() => setViewMode("preview")} />

        {/* ✅ Slide Between Preview and Booking */}
        <SlideFade in={viewMode === "preview"} offsetY="20px">
          {viewMode === "preview" && (
            <ModalBody p={4} bg={bgColor} color={textColor}>
              <Box
                overflowY="auto"
                overflowX="hidden"
                maxH="60vh"
                w="full"
                px={2}
                mt={2}
                >
              {/* Profile Image */}
              <Flex justify="center">
                <Image
                  src={creator.avatar || "/placeholder.jpg"}
                  alt={creator.name}
                  boxSize="80px"
                  borderRadius="full"
                  objectFit="cover"
                  border="3px solid white"
                  borderColor={textColor}
                />
              </Flex>
              
              {/* Name & Rating */}
              <VStack spacing={1} mt={2} textAlign="center">
                <Text fontSize="xl" fontWeight="bold">
                  {creator.first_name} {creator.last_name}
                </Text>
                <Flex align="center">
                  <Icon as={FaPalette} color="purple.400" mr={1} />
                  <Text fontSize="sm">{creator.photography_style || "N/A"}</Text>
                </Flex>
                <Badge colorScheme="purple" fontSize="md">
                  ${creator.price}/hr
                </Badge>
                {/* Scrollable Content */}
                
                {/* ✅ 3D Rotating Image Carousel with Clickable Images */}
                {loading ? (
                  <Spinner size="lg" color="purple.400" />
                ) : error ? (
                  <Text fontSize="sm" color="red.400">
                    {error}
                  </Text>
                ) : media.photos.length > 0 ? (
                  <Box w="full" px={2} mt={2}>
                    <ScrollWrapper>
                      {media.photos.slice(0, 10).map((img, index) => (
                        <ImageCard
                          key={index}
                          src={img.displayUrl}
                          alt={`Image ${index}`}
                          onClick={() => setSelectedImage(img.displayUrl)}
                        />
                      ))}
                    </ScrollWrapper>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.400">
                    No photos available.
                  </Text>
                )}
              </VStack>

              {/* Collapsible Details */}
              <Button
                variant="ghost"
                w="full"
                mt={3}
                onClick={onToggle}
                rightIcon={detailsOpen ? <FaChevronUp /> : <FaChevronDown />}
              >
                {detailsOpen ? "Hide Details" : "View Details"}
              </Button>

              <Collapse in={detailsOpen} animateOpacity>
                <Box p={4} mt={2} bg="gray.800" borderRadius="md" textColor={"white"}>
                  <Flex align="center">
                    <Icon as={FaMapMarkerAlt} color="purple.400" mr={1} />
                    <Text fontSize="sm" >{creator.location || "Unknown"}</Text>
                  </Flex>
                  <Text fontSize="sm">
                    <strong>Camera:</strong> {creator.camera || "Not specified"}
                  </Text>
                  <Text fontSize="sm">
                    <strong>Equipment:</strong> {creator.equipment || "Not specified"}
                  </Text>
                </Box>
              </Collapse>
							{/* ✅ Lightbox Modal for Full Image View (Fixed Size) */}
							<Modal isOpen={!!selectedImage} onClose={() => setSelectedImage(null)}>
								<ModalOverlay />
								<ModalContent
									
									maxW="420px"
									maxH="500px"
									display="flex"
									justifyContent="center"
									alignItems="center"
									bg={bgColor}
									borderRadius="lg"
									boxShadow="2xl"
									p={4}
									position="fixed"
									top="15%"
									transform="translate(-50%, -50%)" // ✅ Centered vertically & horizontally
								>
									<ModalCloseButton color={textColor} size="lg" />
									<ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
										{selectedImage && (
											<Box borderRadius="md" overflow="hidden">
												<Image
													src={selectedImage}
													alt="Selected Photo"
													maxH="450px"
													maxW="450px"
													objectFit="contain"
													borderRadius="md"
												/>
											</Box>
										)}
									</ModalBody>
								</ModalContent>
							</Modal>

              {/* ✅ Book Now Button - Switches View */}
              <Button
                colorScheme="purple"
                mt={4}
                w="full"
                size="lg"
                borderRadius="full"
                onClick={handleAddToCart}
              >
                Add To Cart
              </Button>
              </Box>
            </ModalBody>
          )}
        </SlideFade>

        {/* ✅ Booking Screen - Slide In */}
        {viewMode === "booking" && (
          <SlideFade in={viewMode === "booking"} offsetY="20px">
            <PhotographerDetail
              photographer={creator}
              onClose={onClose} // Close the entire modal on exit
							goBack={() => setViewMode("preview")} // Back to preview mode
            />
          </SlideFade>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PhotographerPreviewModal;






const ScrollWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  max-width: 100%;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  gap: 0.75rem;
  padding: 0.75rem 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ImageCard = styled.img`
  flex: 0 0 auto;
  scroll-snap-align: start;
  width: 100px;
  height: 140px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #ccc;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
    border-color: #805ad5;
  }
`;

